import useUserInfo, { type UserInfo } from '../../auth/useUserInfo';
import { useShallowSelector } from '../../lib/reduxHooks';
import { UserOrigins } from '../../lib/types';
import type { IRootState } from '../../store';
import type { ISettings } from '../../store/pageFrame/types';
import type { LaunchDarklyFlags } from '../launchDarkly';

/**
 * StartSafe accounts do not get in-person signing. Not yet knowing counts as
 * StartSafe so the touchpoints never appear and then vanish once settings land,
 * which is the same rule userInfo follows below.
 */
export function isStartSafeAccount(settings: ISettings | undefined, settingsFetched: boolean): boolean {
  return !settingsFetched || settings?.account?.userOrigin === UserOrigins.DOP;
}

/**
 * The account-level half of the gate: the flag, a resolved user, not StartSafe,
 * and not support impersonation. Null userInfo is a no, not a maybe, since it
 * arrives null on the first renders.
 *
 * isDelegate only catches support impersonation, where the rest of userInfo
 * describes the person being acted for. A Forms permission delegate arrives as
 * themselves with no such claim and passes this, so a caller that knows which
 * envelope it is offering must also check isEnvelopeOwner below.
 *
 * Pure on purpose. Both touchpoints have to agree, and the envelope list calls
 * this directly from a component that already holds everything it needs.
 */
export function canSignInPerson(
  flags: LaunchDarklyFlags | undefined,
  userInfo: UserInfo | null,
  startSafe: boolean
): boolean {
  return Boolean(flags?.inPersonSigning) && userInfo != null && !userInfo.isDelegate && !startSafe;
}

/**
 * Whether the signed-in user owns this envelope, by the sender id both sides carry.
 *
 * SK-2687: a Forms permission delegate reads as isDelegate false, so without this they were offered
 * a hand-off that the service then refused with 403 - a link that always errors. The rule has not
 * changed, only where it is applied.
 *
 * The id rather than the email: an address change would otherwise hide the action from the actual
 * owner, and ids are opaque, so they are compared exactly rather than case-folded.
 *
 * Still paired with canSignInPerson's isDelegate check rather than replacing it, because each
 * catches a population the other misses. Under support impersonation the caller's senderId IS the
 * owner's, so this function answers true for them and only isDelegate refuses them. A Forms
 * permission delegate is the reverse: isDelegate reads false, and only this comparison refuses them.
 */
export function isEnvelopeOwner(userInfo: UserInfo | null, envelopeSenderId: string | undefined): boolean {
  const caller = userInfo?.senderId?.trim();
  const owner = envelopeSenderId?.trim();

  return Boolean(caller && owner && caller === owner);
}

export function useCanSignInPerson(flags?: LaunchDarklyFlags): boolean {
  const { settings, settingsFetched } = useShallowSelector((state: IRootState) => ({
    settings: state.pageFrame.settings,
    settingsFetched: state.pageFrame.settingsFetched,
  }));
  return canSignInPerson(flags, useUserInfo(), isStartSafeAccount(settings, settingsFetched));
}

/**
 * Both halves for a caller that knows which envelope it is offering, in one
 * useUserInfo -- calling the two hooks side by side would fire the provisioning
 * POST twice.
 *
 * The envelope list cannot use this: IEnvelopeMetadata carries no sender
 * identity, only senderCanSign, so there is nothing to compare. It is protected
 * instead by only ever listing the caller's own envelopes, which is a property
 * of the query rather than a check here.
 */
export function useCanSignInPersonOn(
  flags: LaunchDarklyFlags | undefined,
  envelopeSenderId: string | undefined
): boolean {
  const { settings, settingsFetched } = useShallowSelector((state: IRootState) => ({
    settings: state.pageFrame.settings,
    settingsFetched: state.pageFrame.settingsFetched,
  }));
  const userInfo = useUserInfo();

  return (
    canSignInPerson(flags, userInfo, isStartSafeAccount(settings, settingsFetched)) &&
    isEnvelopeOwner(userInfo, envelopeSenderId)
  );
}
